import type { FirebaseError } from 'firebase/app'

import { z } from 'zod'
import { toast } from 'sonner'
import { useState } from 'react'
import { Loader2 } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { Input } from '@/components/ui/input'
import axios, { endpoints } from '@/lib/axios'
import { Button } from '@/components/ui/button'
import { changePassword } from '@/lib/firebase'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

// --- Types ---
interface NewPasswordFormValues {
  newPassword: string
  confirmPassword: string
}

const newPasswordSchema = z
  .object({
    newPassword: z.string().min(6, 'รหัสผ่านต้องมีอย่างน้อย 6 ตัวอักษร'),
    confirmPassword: z.string().min(1, 'กรุณายืนยันรหัสผ่าน'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'รหัสผ่านไม่ตรงกัน',
    path: ['confirmPassword'],
  })

interface NewPasswordDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  currentPassword: string
}

export default function NewPasswordDialog({
  open,
  onOpenChange,
  currentPassword,
}: NewPasswordDialogProps) {
  const [loading, setLoading] = useState(false)

  const form = useForm<NewPasswordFormValues>({
    resolver: zodResolver(newPasswordSchema),
    defaultValues: {
      newPassword: '',
      confirmPassword: '',
    },
  })

  const handleChangePassword = async (formValue: NewPasswordFormValues) => {
    setLoading(true)
    try {
      await changePassword(currentPassword, formValue.newPassword)

      await axios.patch(endpoints.auth.me, {
        needChangePassword: false,
      })

      toast.success('เปลี่ยนรหัสผ่านสำเร็จ')
      form.reset()
      onOpenChange(false)
    } catch (error: unknown) {
      console.error('Change password error:', error)

      if (error instanceof Error && 'code' in error) {
        const firebaseError = error as FirebaseError
        if (firebaseError.code === 'auth/weak-password') {
          toast.error('รหัสผ่านใหม่ไม่แข็งแรงพอ')
        } else {
          toast.error('เกิดข้อผิดพลาดในการเปลี่ยนรหัสผ่าน')
        }
      } else {
        toast.error('เกิดข้อผิดพลาดในการเปลี่ยนรหัสผ่าน')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    form.reset()
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent aria-describedby="" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-center">รหัสผ่านใหม่</DialogTitle>
          <DialogDescription>
            กรุณากรอกรหัสผ่านใหม่ที่ต้องการเปลี่ยน
          </DialogDescription>
        </DialogHeader>

        <form
          onSubmit={form.handleSubmit(handleChangePassword)}
          className="space-y-4"
        >
          <div className="space-y-2">
            <Input
              placeholder="รหัสผ่านใหม่"
              type="password"
              {...form.register('newPassword')}
              disabled={loading}
            />
            {form.formState.errors.newPassword && (
              <p className="text-sm text-red-500">
                {form.formState.errors.newPassword.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Input
              placeholder="ยืนยันรหัสผ่านใหม่"
              type="password"
              {...form.register('confirmPassword')}
              disabled={loading}
            />
            {form.formState.errors.confirmPassword && (
              <p className="text-sm text-red-500">
                {form.formState.errors.confirmPassword.message}
              </p>
            )}
          </div>

          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              'เปลี่ยนรหัสผ่าน'
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  )
}
